import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserAvatar } from 'Components/UserAvatar';
import SolutionPicker from 'Components/SolutionPicker';
import { useStoreState } from 'easy-peasy';
import SignOutModal from './SignOutModal';
import { useToggle } from 'react-use';
import dayjs from 'dayjs';
import Tooltip from 'Components/nui/Tooltip';
import classNames from 'classnames';
import { useTimezone } from '~/timezone';
import { toast } from 'react-toastify';
import { Button } from 'Components/nui';
import Loading from 'Components/nui/Loading';
import { useIntl, FormattedMessage } from 'react-intl';
import { formatTimezone } from '../utils/timezoneFormatter';

const Time = () => {
  const [time, setTime] = useState(dayjs());
  const storeTimeZone = useStoreState(state => state.auth.user.timezone);
  const deviceTimeZone = dayjs.tz.guess();
  const [loading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const locale = useStoreState(state => state?.auth?.user?.locale ?? 'en');

  const timezone = useTimezone();

  // Format timezone names for user-friendly display
  const formattedStoreTimeZone = formatTimezone(storeTimeZone, locale);
  const formattedDeviceTimeZone = formatTimezone(deviceTimeZone, locale);

  const handleClick = async () => {
    setLoading(true);
    const { success } = await timezone.change(deviceTimeZone);
    setLoading(false);

    if (success)
      toast.success(
        formatMessage(
          {
            id: 'timezone-update-success',
            defaultMessage:
              'Your email notification timezone successfully updated to your device timezone ({deviceTimeZone})',
          },
          { deviceTimeZone: formattedDeviceTimeZone }
        )
      );
    else
      toast.error(
        formatMessage({
          id: 'timezone-update-error',
          defaultMessage: 'Could not update timezone.',
        })
      );
  };

  useEffect(() => {
    const tick = setInterval(() => void setTime(dayjs()), 30000);
    return () => void clearInterval(tick);
  }, []);

  return (
    <div className="timezone-wrapper">
      <div className="timezone">
        <span>
          <strong
            className={classNames('all-black', {
              mismatch: storeTimeZone !== deviceTimeZone,
            })}
          >
            <FormattedMessage
              id="navbar-button-change-timezone"
              defaultMessage="Timezone"
            />
          </strong>
        </span>

        {storeTimeZone !== deviceTimeZone ? (
          <Tooltip
            placement="bottom"
            title={
              <span className="icon-tooltip">
                <span className="icon-info-circled" />
                <FormattedMessage
                  id="navbar-tooltip-diff-timezone"
                  defaultMessage="Your email notification timezone ({storeTimeZone}) is different from your device time zone ({deviceTimeZone})."
                  values={{
                    storeTimeZone: formattedStoreTimeZone,
                    deviceTimeZone: formattedDeviceTimeZone,
                  }}
                />
                {loading ? (
                  <Loading size="small" />
                ) : (
                  <Button type="buttonlink" onClick={handleClick}>
                    <FormattedMessage
                      id="navbar-update-timezone"
                      defaultMessage="Update to"
                    />
                    {formattedDeviceTimeZone}
                  </Button>
                )}
              </span>
            }
          >
            <span
              className={classNames({
                mismatch: storeTimeZone !== deviceTimeZone,
              })}
            >
              {formattedDeviceTimeZone}
            </span>
            <span className="icon-attention inline-block" />
          </Tooltip>
        ) : (
          <span className="">{formattedDeviceTimeZone}</span>
        )}
      </div>
      <div className="local-timezone">
        <span>
          <strong className="all-black">
            <FormattedMessage
              id="navbar-button-change-local-time"
              defaultMessage="Local time"
            />
          </strong>
        </span>
        {time.format('h:mm a')}
      </div>
    </div>
  );
};

export default () => {
  const user = useStoreState(state => state.auth.userModel);
  const solution = useStoreState(state => state.auth.solution);
  const { formatMessage } = useIntl();

  const [on, toggle] = useToggle();

  /**Bond please change the className whatever you want */

  return (
    <header className="header">
      <button className="icon-menu hide-for-medium">
        <FormattedMessage id="header-menu-button" defaultMessage="Menu" />
      </button>

      <div className="logobar inline valign-m no-ws">
        <Link to="/" className="nd inline-block">
          {solution.logo_wide ? (
            <>
              <img
                className="logo-home hide-for-large notr"
                src={solution.logo}
                alt={formatMessage({
                  id: 'header-logo-alt',
                  defaultMessage: 'Home | Nui Markets',
                })}
              />
              <img
                className="logo-home show-for-large notr"
                src={solution.logo_wide}
                alt={formatMessage({
                  id: 'header-logo-alt',
                  defaultMessage: 'Home | Nui Markets',
                })}
              />
            </>
          ) : (
            <img
              className="logo-home"
              src={
                solution.logo
                  ? solution.logo
                  : 'https://cream-media.nuimarkets.com/logo/nui.png'
              }
              alt={formatMessage({
                id: 'header-logo-alt',
                defaultMessage: 'Home | Nui Markets',
              })}
            />
          )}
        </Link>
      </div>

      <SolutionPicker />

      <div className="acc account-area nowrap">
        <div id="account-area" className="user acc-user">
          <Time />
          <div className="account-details">
            <UserAvatar />
            <Link to="/account">
              <span className="name">{user._fullname}</span>
            </Link>
            <Link to="/account/solutions" className="solution-name">
              <span>{solution.name}</span>
            </Link>
          </div>
        </div>

        <a
          href="#signout"
          title={formatMessage({
            id: 'header-signout-title',
            defaultMessage: 'Sign out',
          })}
          className="icon-logout signout nowrap"
          onClick={toggle}
        >
          <span>
            <FormattedMessage
              id="navbar-button-Sign out"
              defaultMessage="Sign out"
            />
          </span>
        </a>

        <SignOutModal on={on} toggle={toggle} />
      </div>
    </header>
  );
};
